import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Handshake, Mail, Loader2, ArrowRight, MapPin, Phone, ChevronDown, LifeBuoy, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { submitVolunteer, submitPartner, submitContact, submitSupportRequest, placesAutocomplete, placesDetails } from "@/lib/api";
import { LEGAL } from "@/data/content";
import { useValidationPopup } from "@/components/ValidationPopup";
import { EMAIL_RE, formatPhone, formatEIN } from "@/lib/format";

const MODES = [
  { id: "volunteer", label: "Volunteer", icon: Heart, desc: "Give your time and talent to programs where you can make the biggest difference." },
  { id: "partner", label: "Partner", icon: Handshake, desc: "Bring your organization alongside ours to expand opportunity at scale." },
  { id: "support", label: "Request Support", icon: LifeBuoy, desc: "Know someone or a cause that could use a hand? Share the opportunity with us." },
  { id: "contact", label: "Contact", icon: Mail, desc: "Have a question or an idea? Send us a note and we'll respond personally." },
];

const inputCls =
  "h-12 rounded-xl border-input bg-secondary dark:bg-background px-4 text-base md:text-sm shadow-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0";
const areaCls =
  "min-h-32 rounded-xl border-input bg-secondary dark:bg-background p-4 text-base md:text-sm shadow-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0";
const selectCls = "h-12 rounded-xl border-input bg-secondary dark:bg-background px-4 text-base md:text-sm focus:ring-2 focus:ring-gold";
const errRing = "border-red-500 ring-2 ring-red-500/70 focus-visible:ring-red-500";

function validateFields(values, fields) {
  const errs = {};
  fields.forEach(({ key, type }) => {
    const v = values[key];
    if (type === "bool") { if (!v) errs[key] = true; }
    else if (type === "array") { if (!Array.isArray(v) || v.length === 0) errs[key] = true; }
    else if (type === "email") { if (!v || !EMAIL_RE.test(String(v).trim())) errs[key] = true; }
    else if (!v || !String(v).trim()) { errs[key] = true; }
  });
  return errs;
}

function popupMessage(errs, base) {
  const keys = Object.keys(errs).filter((k) => errs[k]);
  if (keys.length === 1 && errs.email) return "Please enter a valid email address — make sure it includes an \u201c@\u201d (e.g. name@email.com).";
  if (keys.length === 1 && errs.consent) return base;
  if (keys.includes("consent") && keys.length > 1) return "Please complete the highlighted fields and provide your consent to continue.";
  return "Please complete the highlighted fields so we can continue.";
}

const INTERESTS = ["Education", "Athletics", "Health & Wellness", "Leadership Development", "Innovation", "Events", "General / Wherever Needed Most"];
const OPPORTUNITY_TYPES = [
  "One-time events", "Ongoing volunteer opportunities", "Short-term projects", "Tutoring / Education support",
  "Mentoring", "Coaching / Athletics", "Health & Wellness programming", "Leadership development programming",
  "Innovation / Technology", "Event planning and support", "Community outreach", "Fundraising support",
  "Administrative / Operational support", "Professional / Skilled services", "Virtual opportunities",
  "Wherever I am most needed", "Other",
];
const AVAILABILITY = ["Weekday mornings", "Weekday afternoons", "Weekday evenings", "Weekends", "Flexible"];
const COMMITMENT = ["One-time opportunity", "Occasionally, as opportunities arise", "A few times per month", "Weekly", "Ongoing / Long-term", "Flexible / Open to discussing"];
const START_OPTIONS = ["Immediately", "Within the next 30 days", "Within the next 1–3 months", "I have a specific start date", "Flexible"];
const YOUTH_EXPERIENCE = ["Yes", "No", "Prefer not to say"];
const ONBOARDING_OPTIONS = ["Yes", "No", "I would like more information"];
const REFERRALS = ["Search engine", "Social media", "Friend or family", "Community event", "Newsletter", "Partner organization", "School / College / University", "Employer", "Other"];
const ENTITY_TYPES = [
  "501(c)(3) Nonprofit Organization",
  "Other Nonprofit / Tax-Exempt Organization",
  "For-Profit Business / Corporation",
  "Limited Liability Company (LLC)",
  "Foundation / Grantmaking Organization",
  "Educational Institution",
  "Government Agency / Public Entity",
  "Community / Civic Organization",
  "Faith-Based Organization",
  "Unincorporated Organization or Group",
  "Individual / Independent Professional",
  "Other",
];
const PARTNERSHIP_OPTIONS = [
  { title: "Fund a Program or Initiative", desc: "Support a specific program, project, or area of our work through financial contributions." },
  { title: "Provide Goods or Services", desc: "Contribute equipment, supplies, technology, professional expertise, venue space, or other resources that support our work." },
  { title: "Support a Community Event", desc: "Help make a Foundation event, youth activity, workshop, or community initiative possible through funding, resources, or other support." },
  { title: "Create Opportunities for Youth", desc: "Provide mentorship, internships, career exposure, workshops, training, educational experiences, or other opportunities for young people." },
  { title: "Volunteer as a Team or Organization", desc: "Bring your employees, members, students, or organization together to contribute time and skills to a Foundation initiative." },
  { title: "Grantmaking & Foundation Partnership", desc: "For foundations, charitable trusts, and other grantmakers interested in funding opportunities aligned with our mission and programs." },
  { title: "Collaborate on a Program", desc: "Work with us to develop or deliver a program, service, resource, or community initiative where our missions align." },
  { title: "Something Else", desc: "Have another idea for working together? Tell us what you have in mind. We're open to exploring partnerships that align with our mission." },
];
const SUBJECTS = ["General Inquiry", "Donations", "Volunteering", "Partnerships", "Media & Press", "Other"];
const REQUESTER_TYPES = ["Parent or Guardian", "Student", "Educator / School", "Nonprofit or Organization", "Community Member", "Other"];
const SUPPORT_FOCUS = ["Education", "Athletics", "Health & Wellness", "Leadership", "Innovation", "Not sure / Other"];
const BENEFICIARY = ["An individual", "A family", "A group or team", "A school or class", "A community", "An organization"];

function L({ children, required }) {
  return (
    <label className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-foreground">
      {children} {required && <span className="text-gold">*</span>}
    </label>
  );
}

function Help({ children }) {
  return <p className="-mt-1 mb-2.5 text-xs leading-relaxed text-muted-foreground">{children}</p>;
}

function Section({ title, children }) {
  return (
    <section className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <h3 className="shrink-0 font-heading text-sm font-bold uppercase tracking-[0.18em] text-gold">{title}</h3>
        <span className="h-px flex-1 bg-gradient-to-r from-gold/40 to-transparent" />
      </div>
      {children}
    </section>
  );
}

function Submit({ loading, children, testId, disabled }) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      data-testid={testId}
      className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-navy-deep transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children} {!loading && <ArrowRight className="h-4 w-4" />}
    </button>
  );
}

function Consent({ checked, onChange, testId, error, action = "request" }) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-xl p-2 text-sm transition-colors ${error ? "text-red-500 ring-2 ring-red-500/60" : "text-muted-foreground"}`}>
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className={`mt-0.5 data-[state=checked]:border-gold data-[state=checked]:bg-gold data-[state=checked]:text-navy-deep ${error ? "border-red-500" : "border-input"}`} />
      <span>I consent to Thefrent Foundation storing my details to respond to my {action}, in accordance with the{" "}
        <a href="/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-gold underline underline-offset-2 hover:opacity-80">Privacy Policy</a>{" "}and{" "}
        <a href="/terms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-gold underline underline-offset-2 hover:opacity-80">Terms of Use</a>.
      </span>
    </label>
  );
}

const slug = (o) => o.toLowerCase().replace(/[^a-z]/g, "");

/* Compact multi-select shown as a dropdown with checkboxes (for "select all that apply" fields). */
function MultiSelect({ options, value, onChange, placeholder, prefix, error, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testId} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={`${selectCls} flex w-full items-center justify-between gap-2 text-left ${error ? errRing : ""} ${value.length ? "text-foreground" : "text-muted-foreground"}`}>
        <span className="truncate">{value.length ? `${value.length} selected` : placeholder}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-gold" : "text-muted-foreground"}`} />
      </button>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testId}-selected`}>
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1.5 rounded-full bg-gold/10 py-1 pl-3 pr-2 text-xs text-gold">
              {v}
              <button type="button" onClick={() => toggle(v)} aria-label={`Remove ${v}`} className="grid h-4 w-4 place-items-center rounded-full transition-colors hover:bg-gold/20">×</button>
            </span>
          ))}
        </div>
      )}
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-auto rounded-xl border border-border bg-popover p-1 shadow-2xl"
            data-testid={`${testId}-panel`}>
            {options.map((o) => {
              const on = value.includes(o);
              return (
                <li key={o}>
                  <button type="button" onClick={() => toggle(o)} data-testid={`${prefix}-${slug(o)}`}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${on ? "bg-gold/10 text-gold" : "text-foreground hover:bg-secondary"}`}>
                    <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors ${on ? "border-gold bg-gold text-navy-deep" : "border-input"}`}>{on && <Check className="h-3 w-3" />}</span>
                    {o}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function PartnershipSelector({ value, onChange }) {
  const [open, setOpen] = useState(null);
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.1em] text-foreground">
        How would you like to partner with us?
      </label>
      <p className="mb-3 text-sm text-muted-foreground">
        Select the option that best fits your interest. Use the chevron to learn more about each partnership type.
      </p>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-input bg-secondary dark:bg-background">
        {PARTNERSHIP_OPTIONS.map((o, i) => {
          const selected = value === o.title;
          const expanded = open === i;
          return (
            <div key={o.title} className={selected ? "bg-gold/[0.07]" : ""}>
              <div className="flex items-stretch">
                <button
                  type="button"
                  onClick={() => onChange(o.title)}
                  data-testid={`partnership-option-${i}`}
                  className="flex flex-1 items-center gap-3 px-4 py-3.5 text-left"
                >
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${selected ? "border-gold" : "border-input"}`}>
                    {selected && <span className="h-2.5 w-2.5 rounded-full bg-gold" />}
                  </span>
                  <span className={`text-sm font-medium ${selected ? "text-gold" : "text-foreground"}`}>{o.title}</span>
                </button>
                <button
                  type="button"
                  aria-label={`More about ${o.title}`}
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : i)}
                  data-testid={`partnership-chevron-${i}`}
                  className="grid w-12 shrink-0 place-items-center border-l border-border text-muted-foreground transition-colors hover:text-gold"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180 text-gold" : ""}`} />
                </button>
              </div>
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden bg-[#faf8f1] dark:bg-white/[0.05]"
                  >
                    <p className="px-4 pb-4 pl-12 pt-1 text-sm leading-relaxed text-foreground/80">{o.desc}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThankYou({ title, message }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => clearTimeout(t);
  }, []);
  return (
    <div ref={ref} className="flex flex-col items-center py-10 text-center" data-testid="thank-you">
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: [0, 1.25, 1] }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="grid h-20 w-20 place-items-center rounded-full bg-gold/15"
      >
        <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}>
          <Heart className="h-10 w-10 text-gold" fill="currentColor" />
        </motion.div>
      </motion.div>
      <h3 className="mt-7 font-heading text-2xl font-bold tracking-tight">{title}</h3>
      <p className="mt-3 max-w-md text-muted-foreground">{message}</p>
    </div>
  );
}

export default function GetInvolved() {
  const [params, setParams] = useSearchParams();
  const mode = ["volunteer", "partner", "support", "contact"].includes(params.get("tab")) ? params.get("tab") : "volunteer";
  const active = MODES.find((m) => m.id === mode);
  const cardRef = useRef(null);
  const selectMode = (id) => setParams((prev) => { prev.set("tab", id); return prev; });

  useEffect(() => {
    if (params.get("tab") && cardRef.current) {
      const y = cardRef.current.getBoundingClientRect().top + window.pageYOffset - 90;
      if (window.lenis && typeof window.lenis.scrollTo === "function") window.lenis.scrollTo(y);
      else window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [params]);

  return (
    <>
      <PageHero label="Get Involved" title="Inspire. Equip." accent="Empower." subtitle="Helping today. Transforming tomorrow. Choose how you'd like to make an impact." />

      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
        <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
          <div className="lg:sticky lg:top-28 lg:h-fit">
            <div className="space-y-2">
              {MODES.map((m) => {
                const on = mode === m.id;
                return (
                  <button key={m.id} onClick={() => selectMode(m.id)} data-testid={`mode-${m.id}`}
                    className={`flex w-full items-start gap-4 rounded-2xl border p-5 text-left transition-colors ${on ? "border-gold bg-gold/[0.06]" : "border-border hover:border-gold/50"}`}>
                    <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full transition-colors ${on ? "bg-gold text-navy-deep" : "bg-secondary text-foreground"}`}>
                      <m.icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="font-heading text-lg font-semibold">{m.label}</span>
                      <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">{m.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-8 rounded-2xl border border-border bg-card p-6 text-sm">
              <p className="flex items-start gap-3 text-muted-foreground"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {LEGAL.email}</p>
              <p className="mt-3 flex items-start gap-3 text-muted-foreground"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {LEGAL.phone}</p>
              <p className="mt-3 flex items-start gap-3 text-muted-foreground"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {LEGAL.principalOffice}</p>
            </div>
          </div>

          <Reveal>
            <div ref={cardRef} className="rounded-3xl border border-border bg-card p-7 md:p-10" data-testid="form-card">
              <div className="mb-8 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-gold/12 text-gold"><active.icon className="h-5 w-5" /></span>
                <div>
                  <h2 className="font-heading text-2xl font-semibold tracking-tight">
                    {mode === "volunteer" && "Volunteer application"}
                    {mode === "partner" && "Partnership inquiry"}
                    {mode === "support" && "Request support"}
                    {mode === "contact" && "Send us a message"}
                  </h2>
                  <p className="text-xs text-muted-foreground">Fields marked <span className="text-gold">*</span> are required.</p>
                </div>
              </div>
              {mode === "volunteer" && <VolunteerForm />}
              {mode === "partner" && <PartnerForm />}
              {mode === "support" && <SupportForm />}
              {mode === "contact" && <ContactForm />}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function VolunteerForm() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const { notify, popup } = useValidationPopup();
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "", state: "",
    opportunity_other: "", commitment: "", start_availability: "", start_date: "",
    skills: "", youth_experience: "", youth_experience_detail: "",
    onboarding_willing: "", motivation: "", additional_info: "", referral: "", referral_other: "",
    consent: false,
  });
  const [interests, setInterests] = useState([]);
  const [opportunityTypes, setOpportunityTypes] = useState([]);
  const [availability, setAvailability] = useState([]);
  const clearErr = (k) => setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clearErr(k); };
  const setFmt = (k, fmt) => (e) => { setF({ ...f, [k]: fmt(e.target.value) }); clearErr(k); };
  const setVal = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); clearErr(k); };
  const ec = (k) => (errors[k] ? errRing : "");

  const onSubmit = async (e) => {
    e.preventDefault();
    const rules = [
      { key: "first_name" }, { key: "last_name" }, { key: "email", type: "email" },
      { key: "interests", type: "array" }, { key: "opportunity_types", type: "array" }, { key: "availability", type: "array" },
      { key: "commitment" }, { key: "start_availability" }, { key: "skills" },
      { key: "onboarding_willing" }, { key: "motivation" }, { key: "consent", type: "bool" },
    ];
    if (opportunityTypes.includes("Other")) rules.push({ key: "opportunity_other" });
    if (f.start_availability === "I have a specific start date") rules.push({ key: "start_date" });
    const errs = validateFields({ ...f, interests, opportunity_types: opportunityTypes, availability }, rules);
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return notify(popupMessage(errs, "Please provide your consent so we can respond to your application."));
    }
    setLoading(true);
    try {
      await submitVolunteer({ ...f, interests, opportunity_types: opportunityTypes, availability });
      setDone(true);
    } catch { notify("We couldn't submit your application just now. Please try again in a moment."); } finally { setLoading(false); }
  };

  if (done) return <ThankYou title="Thank you for stepping up." message="Your application has been received for review. Our volunteer team will evaluate it against current and upcoming opportunities and follow up with a meaningful next step." />;

  return (
    <>
    {popup}
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-10" data-testid="volunteer-form">
      <Section title="Your details">
        <div className="grid gap-6 sm:grid-cols-2">
          <div><L required>First Name</L><Input className={`${inputCls} ${ec("first_name")}`} data-testid="volunteer-first" value={f.first_name} onChange={set("first_name")} placeholder="Jane" /></div>
          <div><L required>Last Name</L><Input className={`${inputCls} ${ec("last_name")}`} data-testid="volunteer-last" value={f.last_name} onChange={set("last_name")} placeholder="Doe" /></div>
          <div><L required>Email</L><Input type="email" className={`${inputCls} ${ec("email")}`} data-testid="volunteer-email" value={f.email} onChange={set("email")} placeholder="jane@email.com" /></div>
          <div><L>Phone</L><Input className={inputCls} data-testid="volunteer-phone" inputMode="tel" value={f.phone} onChange={setFmt("phone", formatPhone)} placeholder="(555) 000-0000" /></div>
          <div><L>City</L><Input className={inputCls} data-testid="volunteer-city" value={f.city} onChange={set("city")} placeholder="Norfolk" /></div>
          <div><L>State</L><Input className={inputCls} data-testid="volunteer-state" value={f.state} onChange={set("state")} placeholder="VA" /></div>
        </div>
      </Section>

      <Section title="Your interests">
        <div>
          <L required>Areas of Interest</L>
          <Help>Select all that apply.</Help>
          <MultiSelect options={INTERESTS} value={interests} onChange={(arr) => { setInterests(arr); clearErr("interests"); }} placeholder="Select your areas of interest" prefix="vol-interest" error={!!errors.interests} testId="volunteer-interests" />
        </div>
        <div>
          <L required>What types of volunteer opportunities are you interested in?</L>
          <Help>Select all that apply.</Help>
          <MultiSelect options={OPPORTUNITY_TYPES} value={opportunityTypes} onChange={(arr) => { setOpportunityTypes(arr); clearErr("opportunity_types"); }} placeholder="Select opportunity types" prefix="vol-opp" error={!!errors.opportunity_types} testId="volunteer-opportunities" />
          {opportunityTypes.includes("Other") && (
            <div className="mt-4" data-testid="vol-opp-other-wrap"><L required>Please describe</L>
              <Input className={`${inputCls} ${ec("opportunity_other")}`} data-testid="volunteer-opportunity-other" value={f.opportunity_other} onChange={set("opportunity_other")} placeholder="Tell us what you have in mind." />
            </div>
          )}
        </div>
      </Section>

      <Section title="Availability & commitment">
        <div>
          <L required>When are you generally available?</L>
          <Help>Select all that apply.</Help>
          <MultiSelect options={AVAILABILITY} value={availability} onChange={(arr) => { setAvailability(arr); clearErr("availability"); }} placeholder="Select your general availability" prefix="vol-avail" error={!!errors.availability} testId="volunteer-availability" />
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="min-w-0">
            <L required>What type of commitment are you looking for?</L>
            <Select value={f.commitment} onValueChange={setVal("commitment")}>
              <SelectTrigger className={`${selectCls} ${ec("commitment")}`} data-testid="volunteer-commitment"><SelectValue placeholder="Select one" /></SelectTrigger>
              <SelectContent>{COMMITMENT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <L required>When would you be available to start?</L>
            <Select value={f.start_availability} onValueChange={setVal("start_availability")}>
              <SelectTrigger className={`${selectCls} ${ec("start_availability")}`} data-testid="volunteer-start"><SelectValue placeholder="Select one" /></SelectTrigger>
              <SelectContent>{START_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {f.start_availability === "I have a specific start date" && (
              <div className="mt-4" data-testid="vol-start-date-wrap"><L required>Preferred start date</L>
                <Input type="date" className={`${inputCls} block w-full min-w-0 ${ec("start_date")}`} data-testid="volunteer-start-date" value={f.start_date} onChange={set("start_date")} />
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title="Experience & fit">
        <div>
          <L required>Relevant skills, experience, or qualifications</L>
          <Help>Tell us about any skills, experience, certifications, education, or interests that may help us identify the right volunteer opportunity for you — e.g. tutoring, teaching, coaching, athletics, mentoring, youth engagement, health and wellness, leadership, technology, event support, fundraising, community outreach, administration, or professional skills.</Help>
          <Textarea className={`${areaCls} ${ec("skills")}`} data-testid="volunteer-skills" value={f.skills} onChange={set("skills")} placeholder="Share your background and strengths." />
        </div>
        <div>
          <L>Have you previously worked or volunteered with youth or other vulnerable populations?</L>
          <Help>Prior experience is helpful for placement but is not required to volunteer.</Help>
          <Select value={f.youth_experience} onValueChange={setVal("youth_experience")}>
            <SelectTrigger className={selectCls} data-testid="volunteer-youth"><SelectValue placeholder="Select one" /></SelectTrigger>
            <SelectContent>{YOUTH_EXPERIENCE.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {f.youth_experience === "Yes" && (
            <div className="mt-4" data-testid="vol-youth-detail-wrap"><L>Please briefly describe your experience</L>
              <Textarea className={areaCls} data-testid="volunteer-youth-detail" value={f.youth_experience_detail} onChange={set("youth_experience_detail")} placeholder="A sentence or two is perfect." />
            </div>
          )}
        </div>
        <div>
          <L required>Volunteer onboarding requirements</L>
          <div className="mb-3 flex items-start gap-3 rounded-xl border border-gold/25 bg-gold/[0.05] p-4">
            <LifeBuoy className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p className="text-sm leading-relaxed text-muted-foreground">Depending on the volunteer role, Thefrent Foundation may require additional onboarding steps such as orientation or training, a volunteer agreement, a waiver, and/or a background screening.</p>
          </div>
          <p className="mb-2.5 text-sm text-foreground">Are you willing to complete any applicable onboarding requirements for the volunteer role you are being considered for?</p>
          <Select value={f.onboarding_willing} onValueChange={setVal("onboarding_willing")}>
            <SelectTrigger className={`${selectCls} ${ec("onboarding_willing")}`} data-testid="volunteer-onboarding"><SelectValue placeholder="Select one" /></SelectTrigger>
            <SelectContent>{ONBOARDING_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Section>

      <Section title="A little more">
        <div><L required>Why do you want to volunteer with Thefrent Foundation?</L><Textarea className={`${areaCls} ${ec("motivation")}`} data-testid="volunteer-motivation" value={f.motivation} onChange={set("motivation")} placeholder="Tell us what draws you to our mission." /></div>
        <div><L>Is there anything else you would like us to know when considering you for volunteer opportunities?</L><Textarea className={areaCls} data-testid="volunteer-additional" value={f.additional_info} onChange={set("additional_info")} placeholder="Optional — anything that would help us place you well." /></div>
        <div><L>How did you hear about us?</L>
          <Select value={f.referral} onValueChange={(v) => { setF({ ...f, referral: v }); clearErr("referral_other"); }}>
            <SelectTrigger className={selectCls} data-testid="volunteer-referral"><SelectValue placeholder="Select one" /></SelectTrigger>
            <SelectContent>{REFERRALS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          {f.referral === "Other" && (
            <div className="mt-4" data-testid="vol-referral-other-wrap"><L>Please tell us</L>
              <Input className={inputCls} data-testid="volunteer-referral-other" value={f.referral_other} onChange={set("referral_other")} placeholder="How did you find us?" />
            </div>
          )}
        </div>
      </Section>

      <Consent action="application" checked={f.consent} onChange={(v) => { setF({ ...f, consent: !!v }); clearErr("consent"); }} testId="volunteer-consent" error={errors.consent} />
      <Submit loading={loading} testId="volunteer-submit">Submit Application</Submit>
    </form>
    </>
  );
}

function AddressAutocomplete({ value, onChange, error }) {
  const [q, setQ] = useState(value || "");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();
  const session = useRef(null);

  const newSession = () => {
    session.current =
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : String(Date.now()) + Math.random().toString(16).slice(2);
  };

  useEffect(() => { newSession(); }, []);

  const onType = (val) => {
    setQ(val);
    onChange(val);
    clearTimeout(timer.current);
    if (val.trim().length < 3) { setSug([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const d = await placesAutocomplete(val, session.current);
        setSug(d.predictions || []);
        setOpen(true);
      } catch { setSug([]); }
    }, 300);
  };

  const pick = async (p) => {
    setQ(p.text);
    onChange(p.text);
    setOpen(false);
    setSug([]);
    try {
      const d = await placesDetails(p.place_id, session.current);
      if (d.formatted_address) { setQ(d.formatted_address); onChange(d.formatted_address); }
    } catch { /* keep the prediction text if details lookup fails */ }
    newSession(); // a session ends after a selection
  };

  return (
    <div className="relative">
      <L required>Address</L>
      <Input
        className={`${inputCls} ${error ? errRing : ""}`}
        data-testid="partner-address"
        value={q}
        onChange={(e) => onType(e.target.value)}
        onFocus={() => sug.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Start typing an address…"
        autoComplete="off"
      />
      {open && sug.length > 0 && (
        <ul className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-auto rounded-xl border border-border bg-popover p-1 shadow-xl" data-testid="address-suggestions">
          {sug.map((p) => (
            <li key={p.place_id}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary">
                <span className="font-medium">{p.main_text || p.text}</span>
                {p.secondary_text && <span className="text-muted-foreground">, {p.secondary_text}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PartnerForm() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const { notify, popup } = useValidationPopup();
  const [f, setF] = useState({ organization: "", legal_name: "", entity_type: "", entity_type_other: "", ein: "", website: "", partnership_type: "", contact_first: "", contact_last: "", title: "", email: "", phone: "", address: "", scope: "", message: "", consent: false });
  const clearErr = (k) => setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clearErr(k); };
  const setFmt = (k, fmt) => (e) => { setF({ ...f, [k]: fmt(e.target.value) }); clearErr(k); };
  const ec = (k) => (errors[k] ? errRing : "");

  const onSubmit = async (e) => {
    e.preventDefault();
    const rules = [
      { key: "organization" }, { key: "entity_type" }, { key: "contact_first" }, { key: "contact_last" },
      { key: "email", type: "email" }, { key: "address" }, { key: "message" }, { key: "consent", type: "bool" },
    ];
    if (f.entity_type === "Other") rules.push({ key: "entity_type_other" });
    const errs = validateFields({ ...f }, rules);
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return notify(popupMessage(errs, "Please provide your consent so we can respond to your partnership inquiry."));
    }
    setLoading(true);
    try {
      await submitPartner(f);
      setDone(true);
    } catch { notify("We couldn't submit your inquiry just now. Please try again in a moment."); } finally { setLoading(false); }
  };

  if (done) return <ThankYou title="Thank you for your commitment." message="We're grateful you want to build alongside us. Our partnerships team will be in touch very soon." />;

  return (
    <>
    {popup}
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6" data-testid="partner-form">
      <div className="flex flex-col gap-6">
        <div>
          <L required>Organization / Entity Name</L>
          <Help>Enter the organization, business, group, or entity you represent. If you are submitting this inquiry as an individual, enter your full name.</Help>
          <Input className={`${inputCls} ${ec("organization")}`} data-testid="partner-org" value={f.organization} onChange={set("organization")} placeholder="e.g. TowneBank, or your full name" />
        </div>
        <div>
          <L>Legal Organization Name</L>
          <Help>If different from the organization or entity name above, enter the full legal or registered name.</Help>
          <Input className={inputCls} data-testid="partner-legal-name" value={f.legal_name} onChange={set("legal_name")} placeholder="Full legal / registered name (optional)" />
        </div>
        <div>
          <L required>Organization / Entity Type</L>
          <Help>Select the option that best describes the organization or entity you represent.</Help>
          <Select value={f.entity_type} onValueChange={(v) => { setF((p) => ({ ...p, entity_type: v })); clearErr("entity_type"); }}>
            <SelectTrigger className={`${selectCls} ${ec("entity_type")}`} data-testid="partner-entity-type"><SelectValue placeholder="Select entity type" /></SelectTrigger>
            <SelectContent>{ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          {f.entity_type === "Other" && (
            <div className="mt-4" data-testid="partner-entity-other-wrap">
              <L required>Please describe the organization or entity type</L>
              <Input className={`${inputCls} ${ec("entity_type_other")}`} data-testid="partner-entity-other" value={f.entity_type_other} onChange={set("entity_type_other")} placeholder="Describe your organization or entity type" />
            </div>
          )}
        </div>
        <div>
          <L>EIN (Employer Identification Number)</L>
          <Help>If applicable, provide your organization&apos;s Employer Identification Number (EIN). Individuals should leave this field blank. Please do not enter a Social Security or ITIN number.</Help>
          <Input className={inputCls} data-testid="partner-ein" inputMode="numeric" value={f.ein} onChange={setFmt("ein", formatEIN)} placeholder="XX-XXXXXXX (optional)" />
        </div>
        <div><L>Website</L><Input className={inputCls} data-testid="partner-website" value={f.website} onChange={set("website")} placeholder="https://" /></div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div><L required>Contact First Name</L><Input className={`${inputCls} ${ec("contact_first")}`} data-testid="partner-first" value={f.contact_first} onChange={set("contact_first")} placeholder="Your first name" /></div>
        <div><L required>Contact Last Name</L><Input className={`${inputCls} ${ec("contact_last")}`} data-testid="partner-last" value={f.contact_last} onChange={set("contact_last")} placeholder="Your last name" /></div>
        <div><L>Title / Role</L><Input className={inputCls} data-testid="partner-title" value={f.title} onChange={set("title")} placeholder="CSR Manager" /></div>
        <div><L required>Email</L><Input type="email" className={`${inputCls} ${ec("email")}`} data-testid="partner-email" value={f.email} onChange={set("email")} placeholder="you@org.com" /></div>
        <div><L>Phone</L><Input className={inputCls} data-testid="partner-phone" inputMode="tel" value={f.phone} onChange={setFmt("phone", formatPhone)} placeholder="(555) 000-0000" /></div>
        <AddressAutocomplete value={f.address} onChange={(v) => { setF((p) => ({ ...p, address: v })); clearErr("address"); }} error={errors.address} />
      </div>
      <PartnershipSelector value={f.partnership_type} onChange={(v) => setF({ ...f, partnership_type: v })} />
      <div><L>Estimated Scope or Budget</L><Input className={inputCls} data-testid="partner-scope" value={f.scope} onChange={set("scope")} placeholder="e.g. $5,000 sponsorship, 20 volunteers…" /></div>
      <div><L required>How would you like to collaborate?</L><Textarea className={`${areaCls} ${ec("message")}`} data-testid="partner-message" value={f.message} onChange={set("message")} placeholder="Share your vision for partnership." /></div>
      <Consent action="inquiry" checked={f.consent} onChange={(v) => { setF({ ...f, consent: !!v }); clearErr("consent"); }} testId="partner-consent" error={errors.consent} />
      <Submit loading={loading} testId="partner-submit">Start the Conversation</Submit>
    </form>
    </>
  );
}

function SupportForm() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const { notify, popup } = useValidationPopup();
  const [f, setF] = useState({ requester_type: "", requester_type_other: "", first_name: "", last_name: "", email: "", phone: "", organization: "", beneficiary: "", focus_area: "", location: "", description: "", consent: false });
  const clearErr = (k) => setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clearErr(k); };
  const setFmt = (k, fmt) => (e) => { setF({ ...f, [k]: fmt(e.target.value) }); clearErr(k); };
  const ec = (k) => (errors[k] ? errRing : "");

  const onSubmit = async (e) => {
    e.preventDefault();
    const rules = [
      { key: "first_name" }, { key: "last_name" }, { key: "email", type: "email" }, { key: "description" }, { key: "consent", type: "bool" },
    ];
    if (f.requester_type === "Other") rules.push({ key: "requester_type_other" });
    const errs = validateFields({ ...f }, rules);
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return notify(popupMessage(errs, "Please provide your consent so we can review your request."));
    }
    setLoading(true);
    try {
      await submitSupportRequest(f);
      setDone(true);
    } catch { notify("We couldn't submit your request just now. Please try again in a moment."); } finally { setLoading(false); }
  };

  if (done) return <ThankYou title="Thank you for bringing this to us." message="We're honored you thought of us. Our team will thoughtfully explore your request to see how it fits our mission. While we can't promise every request can be fulfilled, we review each one with care — and we'll be in touch." />;

  return (
    <>
    {popup}
    <div className="mb-7 flex items-start gap-3 rounded-2xl border border-gold/25 bg-gold/[0.05] p-5" data-testid="support-note">
      <LifeBuoy className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
      <p className="text-sm leading-relaxed text-muted-foreground">
        Know a person, family, team, or cause that could use a hand? Share it with us. We can’t guarantee every
        request, but if it aligns with our mission we’ll explore it thoughtfully and reach out. There’s no pressure —
        just possibility.
      </p>
    </div>
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6" data-testid="support-form">
      <div><L>I am a…</L>
        <Select value={f.requester_type} onValueChange={(v) => { setF({ ...f, requester_type: v }); clearErr("requester_type_other"); }}>
          <SelectTrigger className={selectCls} data-testid="support-requester-type"><SelectValue placeholder="Select what best describes you" /></SelectTrigger>
          <SelectContent>{REQUESTER_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {f.requester_type === "Other" && (
        <div data-testid="support-other-wrap"><L required>Please tell us a bit about you</L>
          <Input className={`${inputCls} ${ec("requester_type_other")}`} data-testid="support-requester-other" value={f.requester_type_other} onChange={set("requester_type_other")} placeholder="e.g. A local business owner, a coach, a faith group…" />
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        <div><L required>First Name</L><Input className={`${inputCls} ${ec("first_name")}`} data-testid="support-first" value={f.first_name} onChange={set("first_name")} placeholder="Your first name" /></div>
        <div><L required>Last Name</L><Input className={`${inputCls} ${ec("last_name")}`} data-testid="support-last" value={f.last_name} onChange={set("last_name")} placeholder="Your last name" /></div>
        <div><L required>Email</L><Input type="email" className={`${inputCls} ${ec("email")}`} data-testid="support-email" value={f.email} onChange={set("email")} placeholder="you@email.com" /></div>
        <div><L>Phone</L><Input className={inputCls} data-testid="support-phone" inputMode="tel" value={f.phone} onChange={setFmt("phone", formatPhone)} placeholder="(555) 000-0000" /></div>
        <div><L>Organization / School</L><Input className={inputCls} data-testid="support-org" value={f.organization} onChange={set("organization")} placeholder="If applicable" /></div>
        <div><L>City / State</L><Input className={inputCls} data-testid="support-location" value={f.location} onChange={set("location")} placeholder="Norfolk, VA" /></div>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <div><L>Who is this for?</L>
          <Select value={f.beneficiary} onValueChange={(v) => setF({ ...f, beneficiary: v })}>
            <SelectTrigger className={selectCls} data-testid="support-beneficiary"><SelectValue placeholder="Select one" /></SelectTrigger>
            <SelectContent>{BENEFICIARY.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><L>Which area does it relate to?</L>
          <Select value={f.focus_area} onValueChange={(v) => setF({ ...f, focus_area: v })}>
            <SelectTrigger className={selectCls} data-testid="support-focus"><SelectValue placeholder="Select a focus area" /></SelectTrigger>
            <SelectContent>{SUPPORT_FOCUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><L required>Tell us about the opportunity</L><Textarea className={`${areaCls} ${ec("description")}`} data-testid="support-description" value={f.description} onChange={set("description")} placeholder="Who could use support, what's the situation, and how you imagine we might help." /></div>
      <Consent checked={f.consent} onChange={(v) => { setF({ ...f, consent: !!v }); clearErr("consent"); }} testId="support-consent" error={errors.consent} />
      <Submit loading={loading} testId="support-submit">Share This Opportunity</Submit>
    </form>
    </>
  );
}


function ContactForm() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const { notify, popup } = useValidationPopup();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "", consent: false });
  const clearErr = (k) => setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clearErr(k); };
  const setFmt = (k, fmt) => (e) => { setF({ ...f, [k]: fmt(e.target.value) }); clearErr(k); };
  const ec = (k) => (errors[k] ? errRing : "");

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateFields({ ...f }, [
      { key: "first_name" }, { key: "last_name" }, { key: "email", type: "email" }, { key: "message" }, { key: "consent", type: "bool" },
    ]);
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return notify(popupMessage(errs, "Please provide your consent so we can respond to your message."));
    }
    setLoading(true);
    try {
      await submitContact({ ...f, subject: f.subject || "General Inquiry" });
      setDone(true);
    } catch { notify("We couldn't send your message just now. Please try again in a moment."); } finally { setLoading(false); }
  };

  if (done) return <ThankYou title="Thank you for reaching out." message="We've received your message and truly appreciate you connecting with us. We'll get back to you personally very soon." />;

  return (
    <>
    {popup}
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6" data-testid="contact-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <div><L required>First Name</L><Input className={`${inputCls} ${ec("first_name")}`} data-testid="contact-first" value={f.first_name} onChange={set("first_name")} placeholder="Your first name" /></div>
        <div><L required>Last Name</L><Input className={`${inputCls} ${ec("last_name")}`} data-testid="contact-last" value={f.last_name} onChange={set("last_name")} placeholder="Your last name" /></div>
        <div><L required>Email</L><Input type="email" className={`${inputCls} ${ec("email")}`} data-testid="contact-email" value={f.email} onChange={set("email")} placeholder="you@email.com" /></div>
        <div><L>Phone</L><Input className={inputCls} data-testid="contact-phone" inputMode="tel" value={f.phone} onChange={setFmt("phone", formatPhone)} placeholder="(555) 000-0000" /></div>
      </div>
      <div><L>Subject</L>
        <Select value={f.subject} onValueChange={(v) => setF({ ...f, subject: v })}>
          <SelectTrigger className={selectCls} data-testid="contact-subject"><SelectValue placeholder="Select a topic" /></SelectTrigger>
          <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><L required>Message</L><Textarea className={`${areaCls} ${ec("message")}`} data-testid="contact-message" value={f.message} onChange={set("message")} placeholder="Write your message…" /></div>
      <Consent action="message" checked={f.consent} onChange={(v) => { setF({ ...f, consent: !!v }); clearErr("consent"); }} testId="contact-consent" error={errors.consent} />
      <Submit loading={loading} testId="contact-submit">Send Message</Submit>
    </form>
    </>
  );
}
